import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { unique, formatTimeBucket } from "./helpers";

export function buildCohortOption({ viz, query, data, palette, base, measure, xKey }: ChartBuildCtx): ChartOption {
  const dims = query.dimensions ?? [];
  const cohortKey = dims[0] ?? xKey;
  const periodKey = query.timeDimension?.member ?? viz.encodings.color ?? "period";
  const cohorts = unique(data.rows.map((r) => String(r[cohortKey])));
  const periods = unique(data.rows.map((r) => String(r[periodKey])));
  const heatData = data.rows.map((r) => [
    periods.indexOf(String(r[periodKey])),
    cohorts.indexOf(String(r[cohortKey])),
    Number(r[measure] ?? 0),
  ]);
  const values = heatData.map((d) => d[2] as number);
  const maxVal = Math.max(0, ...values);
  const minVal = Math.min(0, ...values);
  const measureFmt = formatFromColumn(measure, data.columns);
  // When the data spans negatives (retention deltas, churn signals)
  // use a diverging colour scale around zero; otherwise a single
  // sequential ramp from neutral → primary.
  const hasNegatives = minVal < 0;
  const visualRange = hasNegatives
    ? Math.max(Math.abs(minVal), maxVal) || 1
    : maxVal || 1;
  return {
    ...base,
    // Reserve room at the bottom for both x-axis labels and the
    // colour-bar legend so they don't overlap.
    grid: { left: 96, right: 16, top: 16, bottom: 64 },
    tooltip: {
      position: "top",
      trigger: "item",
      formatter: (p: { value: [number, number, number] }) => {
        const [pi, ci, v] = p.value;
        return `${cohorts[ci]} · ${periods[pi]}<br/><b>${humanizeMeasure(measure)}:</b> ${formatValue(v, measureFmt)}`;
      },
    },
    xAxis: {
      type: "category",
      data: periods,
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        // Periods are typically time-derived (member is the timeDim).
        // Format ISO datetimes through formatTimeBucket so "2026-04-15
        // T00:00:00" reads as "Apr '26" / "Apr 15".
        formatter: (v: string) => {
          if (typeof v !== "string") return v;
          const formatted = formatTimeBucket(v, query.timeDimension?.granularity);
          return formatted.length > 14 ? formatted.slice(0, 13) + "…" : formatted;
        },
      },
    },
    yAxis: {
      type: "category",
      data: cohorts,
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        // Strip leaking cube-path prefix ("Recruiter.createdAt-2026-04"
        // → "2026-04") + format dates that look ISO-like.
        formatter: (v: string) => {
          if (typeof v !== "string") return v;
          const cleaned = v.replace(/^[A-Z][A-Za-z]+\.[a-zA-Z]+[-_]/, "");
          const dated = formatTimeBucket(cleaned, "month");
          return dated.length > 18 ? dated.slice(0, 17) + "…" : dated;
        },
      },
    },
    visualMap: {
      min: hasNegatives ? -visualRange : 0,
      max: visualRange,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 8,
      itemWidth: 12,
      itemHeight: 100,
      inRange: hasNegatives
        ? { color: ["#b20859", "#f1f5f9", palette[0]] }
        : { color: ["#f1f5f9", palette[0]] },
      text: [humanizeMeasure(measure), ""],
      textStyle: { color: "#6b7280", fontSize: 10 },
      formatter: (v: number) => formatValue(v, measureFmt),
    },
    series: [
      {
        type: "heatmap",
        data: heatData,
        label: {
          show: true,
          color: "#1f2937",
          fontSize: 10,
          // Cell values are 4-char max when we round to integer or
          // compact-format; smaller font + format keeps them inside.
          formatter: (p: { value: [number, number, number] }) => {
            const v = p.value[2];
            if (measureFmt === "percent" || measureFmt === "currency") {
              return formatValue(v, measureFmt);
            }
            // Counts: compact for >= 1000.
            return Math.abs(v) >= 1000
              ? formatValue(v, "compact")
              : formatValue(Math.round(v), "number");
          },
        },
      },
    ],
  } as ChartOption;
}
